import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import PageWithHeader from 'layouts/PageWithHeader/PageWithHeader';
import { CardComponent, LinkComponent, TableComponent } from 'components';
import { DEFAULT_TABLE_PARAMS } from 'components/TableComponent/constants';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { maintenanceModel } from 'models';
import { ROUTES } from 'routes/constants';
import { MigrationReport, MigrationReportRunParams } from 'types';
import { TableData, TableParams, TableRow } from 'types/other';

import { ACTIVE_STATUSES, REPORTS_LIMIT, REPORTS_POLL_INTERVAL_MS } from './constants';
import { formatCount, formatReportDate, formatReportRange } from './helpers';
import { ReportStatusChip } from './ReportStatusChip';
import { RunReportForm } from './RunReportForm';

const hasValue = (value: number | null | undefined): value is number => value !== null && value !== undefined;

export const MigrationReports = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleServerError } = useServerErrorActions();

  const [reports, setReports] = useState<MigrationReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableParams, setTableParams] = useState<TableParams>({ ...DEFAULT_TABLE_PARAMS });

  const fetchReports = useCallback(
    () =>
      maintenanceModel
        .getMigrationReports(REPORTS_LIMIT)
        .then(setReports)
        .catch(handleServerError)
        .finally(() => setLoading(false)),
    [handleServerError],
  );

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const hasActiveReports = useMemo(
    () => reports.some(report => ACTIVE_STATUSES.includes(report.status)),
    [reports],
  );

  useEffect(() => {
    if (!hasActiveReports) return undefined;

    const interval = setInterval(fetchReports, REPORTS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [hasActiveReports, fetchReports]);

  const runReport = (params: MigrationReportRunParams) =>
    maintenanceModel
      .runMigrationReport(params)
      .then(report => {
        setReports(prev => [report, ...prev]);
        setTableParams(prev => ({ ...prev, page: 0 }));
      })
      .catch(handleServerError);

  const pageReports = useMemo(() => {
    const start = tableParams.page * tableParams.perPage;
    return reports.slice(start, start + tableParams.perPage);
  }, [reports, tableParams]);

  const tableData = useMemo<TableData>(
    () => ({
      headers: [
        t('pages.migrationReports.table.status'),
        t('pages.migrationReports.table.range'),
        t('pages.migrationReports.table.planograms'),
        t('pages.migrationReports.table.versions'),
        t('pages.migrationReports.table.skipped'),
        t('pages.migrationReports.table.problems'),
        t('pages.migrationReports.table.created'),
        t('pages.migrationReports.table.finished'),
      ],
      rows: pageReports.map<TableRow>(report => ({
        onClick: () => history.push(`${ROUTES.migrationReportNamespace}${report.id}`),
        cells: [
          <ReportStatusChip status={report.status} />,
          <>{formatReportRange(report)}</>,
          <>{formatCount(report.planogramsCount)}</>,
          <>{formatCount(report.versionsCount)}</>,
          <>{formatCount(report.skippedVersionsCount)}</>,
          <>
            {formatCount(report.errorsCount)}
            {hasValue(report.versionsWithErrorsCount) &&
              ` (${report.versionsWithErrorsCount} ${t('pages.migrationReports.table.versionsShort')})`}
          </>,
          <>{formatReportDate(report.enqueuedAt)}</>,
          <>{formatReportDate(report.finishedAt)}</>,
        ],
      })),
    }),
    [pageReports, t, history],
  );

  return (
    <PageWithHeader tabTitle={t('pages.migrationReports.title')} headerText={t('pages.migrationReports.title')}>
      <Grid container spacing={4} direction="column">
        <Grid item>
          <LinkComponent to={ROUTES.maintenance}>{t('pages.migrationReports.backToMaintenance')}</LinkComponent>
        </Grid>
        <Grid item>
          <RunReportForm onRun={runReport} />
        </Grid>
        <Grid item>
          <CardComponent title={t('pages.migrationReports.list.title')} sm={12} isLoading={loading} useSpinner>
            {!loading && reports.length === 0 ? (
              <div>{t('pages.migrationReports.list.empty')}</div>
            ) : (
              <TableComponent
                data={tableData}
                count={reports.length}
                tableParams={tableParams}
                onParamsChange={params => setTableParams({ ...tableParams, ...params })}
              />
            )}
          </CardComponent>
        </Grid>
      </Grid>
    </PageWithHeader>
  );
};
